export const WORLD_WIDTH = 1280;
export type Surface = { id: string; left: number; right: number; depth: number; height: number; halfDepth: number; endHeight?: number };
export type MotionWorld = { width: number; surfaces: readonly Surface[]; ladder: typeof LADDER };
export const surfaceHeight = (s: Surface, x: number): number => s.height + ((s.endHeight ?? s.height) - s.height) * Math.max(0, Math.min(1, (x-s.left)/(s.right-s.left)));
export const WALKWAYS: readonly Surface[] = [
  { id: 'porch', left: 180, right: 425, depth: -36, height: 96, halfDepth: 24 },
  { id: 'pier', left: 465, right: 705, depth: -36, height: 64, halfDepth: 24 },
  ...[24, 48, 72, 96].map((height, i) => ({ id: `step-${i}`, left: 790 + i * 52, right: i === 3 ? 1190 : 842 + i * 52, depth: -36, height, halfDepth: 24 })),
];
export const LADDER = { x: 245, depth: -36, height: 96 };
export type MotionInput = { axis: number; depth: number; sprint?: boolean };
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const approach = (value: number, target: number, step: number) => value < target ? Math.min(target, value + step) : Math.max(target, value - step);

/** Coordinates are world units; height is altitude, depth is position across the path. */
export class Locomotion {
  x: number;
  depth = 0;
  height = 0;
  vx = 0;
  vz = 0;
  vy = 0;
  grounded = true;
  climbing = false;
  facing: -1 | 1 = 1;
  landingSpeed = 0;
  landingPulse = 0;
  gait = 0;
  private coyote = .1;
  private jumpBuffer = 0;
  private dropTimer = 0;
  private dodgeTimer = 0;

  constructor(x = WORLD_WIDTH * .38, readonly world: MotionWorld = { width: WORLD_WIDTH, surfaces: WALKWAYS, ladder: LADDER }) { this.x = x; }
  jump(drop = false): void {
    if (drop && this.grounded && this.height > 0) {
      this.dropTimer = .3; this.grounded = false; this.vy = -60; this.height -= 2;
    } else this.jumpBuffer = .13;
  }
  dodge(): void { this.dodgeTimer = .2; this.vx = this.facing * 610; }
  get dodging(): boolean { return this.dodgeTimer > 0; }
  get mode(): 'idle' | 'walk' | 'jump' | 'fall' | 'climb' | 'dodge' {
    if (this.climbing) return 'climb';
    if (this.dodging) return 'dodge';
    if (!this.grounded) return this.vy > 0 ? 'jump' : 'fall';
    return Math.hypot(this.vx, this.vz) > 12 ? 'walk' : 'idle';
  }
  step(seconds: number, input: MotionInput): void {
    const duration = clamp(seconds, 0, .1);
    const count = Math.max(1, Math.ceil(duration / (1 / 120)));
    this.landingSpeed = 0;
    for (let i = 0; i < count; i++) this.integrate(duration / count, input);
  }
  private integrate(dt: number, raw: MotionInput): void {
    const LADDER = this.world.ladder;
    const length = Math.max(1, Math.hypot(raw.axis, raw.depth));
    const axis = clamp(raw.axis / length, -1, 1), depth = clamp(raw.depth / length, -1, 1);
    this.jumpBuffer = Math.max(0, this.jumpBuffer - dt);
    this.dropTimer = Math.max(0, this.dropTimer - dt);
    this.dodgeTimer = Math.max(0, this.dodgeTimer - dt);
    this.landingPulse = Math.max(0, this.landingPulse - dt * 5);
    this.coyote = this.grounded ? .1 : Math.max(0, this.coyote - dt);
    if (axis !== 0 && !this.climbing) this.facing = axis < 0 ? -1 : 1;
    const atLadder = Math.abs(this.x - LADDER.x) < 24 && Math.abs(this.depth - LADDER.depth) < 30;
    if (!this.climbing && atLadder && Math.abs(axis) < .1 && this.jumpBuffer === 0 &&
      ((depth < -.1 && this.height < LADDER.height) || (depth > .1 && this.height > 0 && this.grounded))) this.climbing = true;
    if (this.climbing) {
      this.x = LADDER.x; this.depth = LADDER.depth; this.vx = this.vz = this.vy = 0;
      this.height = clamp(this.height - depth * 110 * dt, 0, LADDER.height);
      this.gait += Math.abs(depth) * dt * 7;
      this.grounded = false;
      if (this.jumpBuffer > 0) { this.climbing = false; this.coyote = .1; }
      else if ((this.height === 0 && depth > 0) || (this.height === LADDER.height && depth < 0)) {
        this.climbing = false; this.grounded = true;
      } else return;
    }
    if (this.jumpBuffer > 0 && this.coyote > 0) {
      this.vy = 530; this.grounded = false; this.coyote = 0; this.jumpBuffer = 0;
    }
    const acceleration = this.grounded ? 1900 : 820;
    const speed=raw.sprint&&this.grounded&&!this.climbing?1.5:1;
    if (!this.dodging) this.vx = approach(this.vx, axis * 260 * speed, (axis === 0 ? 2200 : acceleration) * dt);
    this.vz = approach(this.vz, depth * 140 * speed, (depth === 0 ? 1400 : acceleration) * dt);
    this.x = clamp(this.x + this.vx * dt, 65, this.world.width - 65);
    this.depth = clamp(this.depth + this.vz * dt, -70, 36);
    const candidates = this.world.surfaces.filter(s => this.x >= s.left && this.x <= s.right && Math.abs(this.depth - s.depth) <= s.halfDepth).map(s => ({...s, height: surfaceHeight(s, this.x)}));
    const terrain=Math.max(0,...candidates.filter(s=>s.endHeight!==undefined).map(s=>s.height));
    if (this.grounded) {
      const support = Math.max(terrain, this.dropTimer > 0 ? 0 : Math.max(0, ...candidates.filter(s => s.height <= this.height + 26).map(s => s.height)));
      if (Math.abs(support - this.height) <= 26) this.height = support;
      else this.grounded = false;
    }
    if (!this.grounded) {
      const before = this.height;
      this.vy -= 1500 * dt;
      this.height += this.vy * dt;
      const landing = Math.max(terrain, this.dropTimer > 0 ? 0 : Math.max(0, ...candidates.filter(s => before >= s.height - .01 && this.height <= s.height && this.vy <= 0).map(s => s.height)));
      if (this.vy <= 0 && this.height <= landing) {
        this.landingSpeed = Math.max(this.landingSpeed, -this.vy); this.landingPulse = clamp(-this.vy / 600, .2, 1);
        this.height = landing; this.vy = 0; this.grounded = true;
      }
    }
    if (this.grounded) this.gait += Math.hypot(this.vx, this.vz) * dt / 34;
  }
}
